import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * "No reference number — save anyway?" for every Tally-integrated save.
 *
 * When a payment mode is set to require a reference number (Tally > Payment Mode Ledgers) and the
 * user left it blank, the API refuses the save with HTTP 400 and `confirmable: true` — nothing is
 * written. This asks the user with the API's own message; on OK the identical request is sent
 * again with X-Allow-Missing-Reference, and the API saves without the reference. On Cancel the
 * error carries on to the screen as before, worded so its toast says what to do.
 *
 * Only a confirmable 400 is touched — every other error, and every other request, passes through
 * unchanged.
 */
@Injectable()
export class MissingReferenceInterceptor implements HttpInterceptor {

  static readonly HEADER = 'X-Allow-Missing-Reference';

  private static readonly ASK = ' Save without the reference number?';

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const body = MissingReferenceInterceptor.parse(error?.error);

        if (error?.status !== 400 || body?.confirmable !== true
            || req.headers.has(MissingReferenceInterceptor.HEADER)) {
          return throwError(error);
        }

        const message: string = body.message || 'Reference number not entered.' + MissingReferenceInterceptor.ASK;

        if (window.confirm(message)) {
          return next.handle(req.clone({ setHeaders: { [MissingReferenceInterceptor.HEADER]: 'true' } }));
        }

        return throwError(new HttpErrorResponse({
          error: {
            ...body,
            message: message.replace(MissingReferenceInterceptor.ASK, '')
              + ' Enter the reference number and save again.',
          },
          headers: error.headers,
          status: error.status,
          statusText: error.statusText,
          url: error.url,
        }));
      })
    );
  }

  /** A body the screen asked for as text still arrives as a JSON string. */
  private static parse(body: any): any {
    if (typeof body !== 'string') {
      return body;
    }

    try {
      return JSON.parse(body);
    } catch {
      return null;
    }
  }
}
